export const ENTITY_ACTIVITY_PARENT_TYPES = {
  invoice: "invoice",
  customer: "customer",
  collection_thread: "collection_thread",
  discount_group: "discount_group",
  cash_receipt: "cash_receipt",
} as const;

export type EntityActivityParentType =
  (typeof ENTITY_ACTIVITY_PARENT_TYPES)[keyof typeof ENTITY_ACTIVITY_PARENT_TYPES];

export const ENTITY_ACTIVITY_ACTOR_TYPES = {
  user: "user",
  system: "system",
  integration: "integration",
} as const;

export type EntityActivityActorType =
  (typeof ENTITY_ACTIVITY_ACTOR_TYPES)[keyof typeof ENTITY_ACTIVITY_ACTOR_TYPES];

export const DISCOUNT_GROUP_ACTIVITY_KINDS = {
  created: "discount_group.created",
  submitted: "discount_group.submitted",
  approved: "discount_group.approved",
  rejected: "discount_group.rejected",
  reverted_to_draft: "discount_group.reverted_to_draft",
  deactivated: "discount_group.deactivated",
  element_deactivated: "discount_group.element_deactivated",
  details_changed: "discount_group.details_changed",
  element_added: "discount_group.element_added",
  element_updated: "discount_group.element_updated",
  element_removed: "discount_group.element_removed",
  line_resolved: "discount_group.line_resolved",
  line_dismissed: "discount_group.line_dismissed",
  customer_resolution: "discount_group.customer_resolution",
  /** One summary line per completed assistant (Nami) action on the group. */
  agent_action: "discount_group.agent_action",
  product_match: "discount_group.product_match",
  /** Synthesized from the source inbound email — never recorded. */
  email_received: "discount_group.email_received",
} as const;

export type DiscountGroupActivityKind =
  (typeof DISCOUNT_GROUP_ACTIVITY_KINDS)[keyof typeof DISCOUNT_GROUP_ACTIVITY_KINDS];

export const CUSTOMER_ACTIVITY_KINDS = {
  note_added: "customer.note_added",
  collection_status_changed: "customer.collection_status_changed",
  comms_preferences_changed: "customer.comms_preferences_changed",
  remittance_type_changed: "customer.remittance_type_changed",
} as const;

/**
 * Per-receipt activity kinds for the cash-receipt timeline
 * (`core_entity_activities`, parentType `cash_receipt`).
 *
 * Only `note_added` is user-authored and mutable; every other kind is a
 * lifecycle record of something that happened to the receipt and is immutable
 * once written — see `cashReceiptActivity.ts`, which enforces that split.
 *
 * Deliberately NOT recorded: receipts created by an ingest (Plaid, Famous,
 * email) and customers auto-assigned during a sync. Those run at ingest
 * volume, and the row's own `createdAt` plus `originSystem` already say when
 * it appeared and where from — the timeline synthesizes an `ingested` entry
 * from them at read time instead.
 */
export const CASH_RECEIPT_ACTIVITY_KINDS = {
  /** The only editable/deletable kind. */
  note_added: "cash_receipt.note_added",
  /** Manual entry through the UI only. */
  created: "cash_receipt.created",
  customer_changed: "cash_receipt.customer_changed",
  payment_linked: "cash_receipt.payment_linked",
  payment_unlinked: "cash_receipt.payment_unlinked",
  remittance_linked: "cash_receipt.remittance_linked",
  /** A hand-picked remittance link was closed, with a reason. */
  remittance_unlinked: "cash_receipt.remittance_unlinked",
  details_edited: "cash_receipt.details_edited",
  /** Another receipt was folded into this one as a duplicate. */
  consolidated: "cash_receipt.consolidated",
  /** An AR user marked that a remittance should have come with this cash. */
  remittance_marked_missing: "cash_receipt.remittance_marked_missing",
  remittance_missing_cleared: "cash_receipt.remittance_missing_cleared",
  /**
   * The receipt's remittance was requested from its customer's portal. Also
   * the record that a request was made: one per receipt and customer.
   */
  remittance_portal_fetch_requested:
    "cash_receipt.remittance_portal_fetch_requested",
  /** Synthesized from the receipt's own createdAt/originSystem — never recorded. */
  ingested: "cash_receipt.ingested",
} as const;

export type CashReceiptActivityKind =
  (typeof CASH_RECEIPT_ACTIVITY_KINDS)[keyof typeof CASH_RECEIPT_ACTIVITY_KINDS];

/** The kinds a user may edit or delete. Everything else is append-only. */
export const MUTABLE_CASH_RECEIPT_ACTIVITY_KINDS: ReadonlySet<string> = new Set(
  [CASH_RECEIPT_ACTIVITY_KINDS.note_added],
);
